import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import oiBackfillService from "@/services/oiBackfillService";
import type { OptionMinutePreviewResponse } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

interface Props {
  symbolCodes: string[];
  fromDate: string;
  toDate: string;
  strikeBand: number;
}

/** "Option prices (1-minute)" jobs: a read-only dry run showing contracts and Breeze calls per day before starting. */
export default function OptionMinutePreview({ symbolCodes, fromDate, toDate, strikeBand }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OptionMinutePreviewResponse | null>(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(await oiBackfillService.previewOptionMinute({
        symbol_codes: symbolCodes, from_date: fromDate, to_date: toDate, strike_band: strikeBand,
      }));
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box data-testid="option-minute-preview">
      <Button variant="outlined" onClick={() => void run()} disabled={loading || symbolCodes.length === 0 || !fromDate || !toDate}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
        {loading ? "Planning…" : "Preview Breeze calls"}
      </Button>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {result && (
        <>
          <Alert severity={result.days_without_index_range > 0 || result.errors.length > 0 ? "warning" : "info"} sx={{ mt: 1 }}
            data-testid="option-minute-preview-summary">
            {result.trading_days} trading day(s) · {result.contracts} contract-day(s) · {result.already_stored} already
            stored · <b>{result.calls_needed} Breeze call(s) needed</b>
            {result.days_without_index_range > 0 &&
              ` · ${result.days_without_index_range} day(s) have no stored index candles: each needs one extra call to plan`}
            {result.errors.length > 0 && ` · ${result.errors.join("; ")}`}. Run it after the market close so it does not
            use Breeze calls the paper runners need.
          </Alert>
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Day</TableCell><TableCell>Index</TableCell><TableCell>Index range</TableCell>
                  <TableCell>Expiries</TableCell><TableCell>Contracts</TableCell><TableCell>Stored</TableCell>
                  <TableCell>Calls</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.days.map((d) => (
                  <TableRow key={`${d.symbol}-${d.day}`}>
                    <TableCell>{d.day}</TableCell><TableCell>{d.symbol}</TableCell>
                    <TableCell>
                      {d.index_low === null ? (
                        <Typography variant="caption" color="warning.main">not stored</Typography>
                      ) : `${d.index_low} – ${d.index_high}`}
                    </TableCell>
                    <TableCell>{d.expiries.join(", ") || "—"}</TableCell><TableCell>{d.contracts}</TableCell>
                    <TableCell>{d.already_stored}</TableCell><TableCell>{d.calls_needed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
